import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteVehicleColor, useFilterVehicleColors } from "../../../../../common/api/queries/vehicleColor";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../../common/constants";
import { SearchPageRequest } from "../../../../../common/types";
import { CertificateVehicleColor } from "../../types";

interface Props {
  searchParams: SearchPageRequest;
  onUpdateClick: (certificateVehicleColor: CertificateVehicleColor) => void;
}

export const VehicleColorTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterVehicleColors(searchParams);
  const deleteVehicleColor = useDeleteVehicleColor();

  const columns: ColumnsType<CertificateVehicleColor> = [
    {
      key: "value",
      title: t("vehicleColor.attrs.value"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.value}</Ellipsis>,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <>
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("vehicleColor.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={async () => {
              await deleteVehicleColor.mutate(record.id);
            }}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card className="card-box">
      <Table<CertificateVehicleColor>
        {...tableStandardProps()}
        columns={columns}
        loading={isFetching}
        dataSource={data?.pageData}
        scroll={{ x: TableSizes.SMALL }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />
    </Card>
  );
};
